import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { IPagination, ISort } from '@/components/table/types';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { FirmInfo } from '../platformSettings/firmInfo';
import AddTenantModal from './modals/addTenant';
import DeleteTenantModal from './modals/deleteTenant';
import EditTenantModal from './modals/editTenant';
import { ADVISORY_FIRMS_QUERY } from './queries';
import TenantsTable from './tenantsTable/table';
import { IAdvisoryFirms, ITenant } from './types';

interface TenantsProps {
  handleClose: () => void;
  backToTitle?: string;
}

export const Tenants = ({ handleClose, backToTitle }: TenantsProps) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ISort>({ key: 'NAME', asc: true });
  const [tenants, setTenants] = useState<ITenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<ITenant | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'add' });
  const [modalPageType, setModalPageType] = useState('default');
  const [paginationData, setPaginationData] = useState<IPagination>({
    total: 0,
    limit: initialValuesFromTable.initialAmountRowsPerPage[0],
    offset: 0
  });

  const { refetch: refetchTenants, networkStatus: advisoryFirmsNetworkStatus } = useQuery<{
    advisoryFirms: IAdvisoryFirms;
  }>(ADVISORY_FIRMS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        search: search,
        sortBy: sort.key,
        sortDirection: sort.asc ? 'ASC' : 'DESC',
        limit: paginationData.limit,
        page: Math.floor(paginationData.offset / paginationData.limit) + 1
      }
    },
    onCompleted: ({ advisoryFirms }) => {
      setPaginationData({
        ...paginationData,
        total: advisoryFirms.total
      });
      setTenants(advisoryFirms.data);
    }
  });

  const openModalWindow = (type: string, tenant?: ITenant) => {
    setModalWindow({ type, isOpen: true });
    setCurrentTenant(tenant || null);
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentTenant(null);
  };

  const handleModalPage = (type: string, tenant?: ITenant) => {
    setModalPageType(type);
    setCurrentTenant(tenant || null);
    if (type === 'default') {
      refetchTenants();
    }
  };

  if (modalPageType !== 'default') {
    return (
      <>
        {modalPageType === 'firmInfo' && (
          <FirmInfo backToTitle="Advisory Firms" tenantId={currentTenant?.id} handleClose={() => handleModalPage('default')} />
        )}
      </>
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'add' && (
        <AddTenantModal isOpen={modalWindow.isOpen} refetchTenants={refetchTenants} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'edit' && (
        <EditTenantModal isOpen={modalWindow.isOpen} tenant={currentTenant} refetchTenants={refetchTenants} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'delete' && (
        <DeleteTenantModal isOpen={modalWindow.isOpen} tenant={currentTenant} refetchTenants={refetchTenants} onClose={closeModalWindow} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
        <PageTitle title="Advisory Firms" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <AddButtonWrapper>
            <Button size="md" onClick={() => openModalWindow('add')} icon={<AddButtonIcon />}>
              Add New Advisory Firm
            </Button>
          </AddButtonWrapper>
          <FilterPage
            search={{
              value: search,
              onChange: setSearch,
              placeholder: 'Search'
            }}
            resultsValue={paginationData.total}
            refetch={() => refetchTenants()}
          />
          <TenantsTable
            tenantsTableTableData={tenants}
            sort={sort}
            setSort={setSort}
            loading={advisoryFirmsNetworkStatus === NetworkStatus.loading || advisoryFirmsNetworkStatus === NetworkStatus.setVariables}
            handleModalPage={handleModalPage}
            openModalWindow={openModalWindow}
          />
          <PaginationWrap>
            <TablePagination
              savePagination={(limit, offset) => setPaginationData({ ...paginationData, limit, offset })}
              paginationValues={paginationData}
              refetch={() => {}}
              usePage
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const AddButtonWrapper = styled.div`
  margin-top: 20px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
